import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';

const NUMERIC_FIELDS = [
  { group: 'Profile' , key: 'name', label: 'Profile name', unit: '', example: 'e.g., Baseline 2026', type: 'text' },
  { group: 'Profile', key: 'chronological_age', label: 'Chronological age', unit: 'years', example: 'e.g., 47', min: 0, max: 130 },
  { group: 'Telomere length', key: 'telomere_length_kb', label: 'Telomere length', unit: 'kb', example: 'e.g., 7.4', min: 0, max: 20 },
  { group: 'Telomere length', key: 'telomere_ts_ratio', label: 'T/S ratio', unit: 'ratio', example: 'e.g., 1.05', min: 0, max: 5 },
  { group: 'Epigenetic clocks', key: 'epigenetic_age_horvath', label: 'Horvath clock', unit: 'years', example: 'e.g., 45.2', min: 0, max: 130 },
  { group: 'Epigenetic clocks', key: 'epigenetic_age_hannum', label: 'Hannum clock', unit: 'years', example: 'e.g., 44.0', min: 0, max: 130 },
  { group: 'Epigenetic clocks', key: 'epigenetic_age_phenoage', label: 'PhenoAge clock', unit: 'years', example: 'e.g., 46.5', min: 0, max: 130 },
  { group: 'Epigenetic clocks', key: 'methylation_age_accel', label: 'Methylation age accel.', unit: 'years', example: 'e.g., 1.2 (positive = older)', min: -50, max: 50 },
  { group: 'Inflammatory markers', key: 'crp_mg_l', label: 'CRP (hs-CRP)', unit: 'mg/L', example: 'e.g., 1.4', min: 0, max: 500 },
  { group: 'Inflammatory markers', key: 'il6_pg_ml', label: 'IL-6', unit: 'pg/mL', example: 'e.g., 2.8', min: 0, max: 1000 },
  { group: 'Inflammatory markers', key: 'tnfa_pg_ml', label: 'TNF-alpha', unit: 'pg/mL', example: 'e.g., 4.1', min: 0, max: 1000 },
  { group: 'Metabolic', key: 'hba1c_percent', label: 'HbA1c', unit: '%', example: 'e.g., 5.4', min: 0, max: 20, step: '0.1' },
  { group: 'Metabolic', key: 'fasting_glucose_mg_dl', label: 'Fasting glucose', unit: 'mg/dL', example: 'e.g., 92', min: 0, max: 600 },
  { group: 'Metabolic', key: 'insulin_uiu_ml', label: 'Fasting insulin', unit: 'µIU/mL', example: 'e.g., 6.5', min: 0, max: 300 },
  { group: 'Lipid panel', key: 'ldl_mg_dl', label: 'LDL-C', unit: 'mg/dL', example: 'e.g., 95', min: 0, max: 500 },
  { group: 'Lipid panel', key: 'hdl_mg_dl', label: 'HDL-C', unit: 'mg/dL', example: 'e.g., 58', min: 0, max: 200 },
  { group: 'Lipid panel', key: 'triglycerides_mg_dl', label: 'Triglycerides', unit: 'mg/dL', example: 'e.g., 120', min: 0, max: 2000 },
];

export default function BiomarkerForm({ value, onChange, namePrefixId = 'biomarker' }) {
  const [errors, setErrors] = useState({});

  const grouped = NUMERIC_FIELDS.reduce((acc, f) => {
    acc[f.group] = acc[f.group] || [];
    acc[f.group].push(f);
    return acc;
  }, {});

  const update = (key, raw, field) => {
    const next = { ...value };
    if (field.type === 'text') {
      next[key] = raw;
      setErrors((e) => ({ ...e, [key]: undefined }));
    } else {
      if (raw === '' || raw === null) {
        next[key] = null;
        setErrors((e) => ({ ...e, [key]: undefined }));
      } else {
        const num = Number(raw);
        if (Number.isNaN(num)) {
          setErrors((e) => ({ ...e, [key]: 'Must be a number.' }));
        } else if (field.min !== undefined && num < field.min) {
          setErrors((e) => ({ ...e, [key]: `Min ${field.min}` }));
        } else if (field.max !== undefined && num > field.max) {
          setErrors((e) => ({ ...e, [key]: `Max ${field.max}` }));
        } else {
          setErrors((e) => ({ ...e, [key]: undefined }));
        }
        next[key] = num;
      }
    }
    onChange(next);
  };

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([group, fields]) => (
        <Card key={group} className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] p-4 sm:p-6">
          <div className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted-fg)]">{group}</div>
            <div className="text-sm text-[color:var(--muted-fg)]">All fields are optional. Leave blank if unknown.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} data-testid={`biomarker-input-group-${f.key}`} className="space-y-1">
                <Label htmlFor={`${namePrefixId}-${f.key}`} className="text-sm font-medium">
                  {f.label} {f.unit && <span className="text-[color:var(--muted-fg)] font-normal">({f.unit})</span>}
                </Label>
                <Input
                  id={`${namePrefixId}-${f.key}`}
                  data-testid={`biomarker-input-${f.key}`}
                  type={f.type === 'text' ? 'text' : 'number'}
                  step={f.step || 'any'}
                  value={value[f.key] ?? ''}
                  onChange={(e) => update(f.key, e.target.value, f)}
                  placeholder={f.example}
                  className="tabular-nums"
                />
                {errors[f.key] ? (
                  <p className="text-xs text-[color:var(--danger)]">{errors[f.key]}</p>
                ) : (
                  <p className="text-[11px] text-[color:var(--muted-fg)]">{f.example}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Card className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] p-4 sm:p-6">
        <div className="mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted-fg)]">Advanced — senescent cell burden</div>
          <div className="text-sm text-[color:var(--muted-fg)]">Free-text. Describe p16^INK4a status or SASP factors (IL-8, MCP-1, MMPs) if available.</div>
        </div>
        <Textarea
          data-testid="biomarker-input-senescent_burden_text"
          rows={3}
          value={value.senescent_burden_text ?? ''}
          onChange={(e) => onChange({ ...value, senescent_burden_text: e.target.value })}
          placeholder="e.g., p16 elevated in T-cell subsets; MMP-3 mildly increased"
        />
      </Card>
    </div>
  );
}
